import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { BROADCAST_CHECK_EVENT, BROADCASTS_CHANGED_EVENT } from "@/components/BroadcastPopup";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

const LINK_BY_TYPE = {
  missed_visit_alert: "/reports/missed-visits",
  visit_flagged: "/live-attendance",
  geo_tag_approved: "/pjp/today",
  geo_tag_rejected: "/pjp/today",
};

// `daily_working_summary` fires for both the SE (self) and their manager,
// same type — the manager's copy carries `se_user_id` in the payload, the
// self copy doesn't, so route each to its own viewer page.
function resolveLink(n) {
  if (n.type === "daily_working_summary") {
    const p = n.payload || n.data || {};
    const date = p.date ? `?date=${encodeURIComponent(p.date)}` : "";
    return p.se_user_id
      ? `/manager/team-day-summaries${date}`
      : `/field/my-day-summary${date}`;
  }
  const p = n.payload || n.data || {};
  if (p.link) return p.link;
  return LINK_BY_TYPE[n.type];
}

/** NotificationBell — header widget for both shells. Polls every 60s. */
export default function NotificationBell() {
  const nav = useNavigate();
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  // Unread broadcast notifications already seen by this bell (null until the
  // first load — BroadcastPopup does its own check on mount).
  const seenBroadcasts = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications", { params: { page_size: 20 } });
      setData(data);
      // A new admin broadcast arrived while the app was open: let the popup
      // look for it now, riding on this poll instead of polling on its own.
      const ids = (data?.items || []).filter((n) => n.type === "broadcast" && !n.read_at).map((n) => n.id);
      if (seenBroadcasts.current === null) seenBroadcasts.current = new Set(ids);
      else if (ids.some((id) => !seenBroadcasts.current.has(id))) {
        ids.forEach((id) => seenBroadcasts.current.add(id));
        window.dispatchEvent(new Event(BROADCAST_CHECK_EVENT));
      }
    } catch (_e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    // Battery: poll every 3 min (not every minute) and not at all while the screen/tab is hidden.
    const t = setInterval(() => { if (document.visibilityState === "visible") load(); }, 180000);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    // acknowledging a broadcast also clears its bell entry — refresh the badge
    window.addEventListener(BROADCASTS_CHANGED_EVENT, load);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener(BROADCASTS_CHANGED_EVENT, load);
    };
  }, [load]);

  const markRead = async (n) => {
    try { await api.post(`/notifications/${n.id}/mark-read`); }
    catch (_e) {}
    const link = resolveLink(n);
    if (link) { setOpen(false); nav(link); }
    load();
  };

  const markAll = async () => {
    try { await api.post("/notifications/mark-all-read"); } catch (_e) {}
    load();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5 text-neutral-600" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white bg-[#E5197F] flex items-center justify-center"
                  data-testid="notification-badge">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-[70vh] overflow-y-auto">
        <div className="flex items-center justify-between px-2 py-1.5 text-xs">
          <div className="font-semibold text-neutral-900">Notifications</div>
          {data.unread > 0 && (
            <button onClick={markAll} className="text-[11px] text-[#E5197F] hover:underline flex items-center gap-1"
                    data-testid="notif-mark-all-read">
              <CheckCheck className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        {data.items.length === 0 ? (
          <div className="py-6 text-center text-xs text-neutral-400">No notifications</div>
        ) : data.items.map((n) => (
          <DropdownMenuItem key={n.id} onClick={() => markRead(n)}
                            data-testid={`notif-item-${n.id.slice(0, 8)}`}
                            className={`flex-col items-start gap-0.5 py-2 ${!n.read_at ? "bg-[#E5197F]/5" : ""}`}>
            {n.type === "broadcast" ? (
              <div className={`text-[11px] uppercase tracking-widest ${(n.payload || n.data || {}).priority === "urgent" ? "text-red-600 font-semibold" : "text-neutral-400"}`}>
                {(n.payload || n.data || {}).priority === "urgent" ? "Urgent message" : "Message"}
              </div>
            ) : (
              <div className="text-[11px] uppercase tracking-widest text-neutral-400">
                {n.type.replace(/_/g, " ")}
              </div>
            )}
            <div className="text-sm font-semibold text-neutral-900">{n.title}</div>
            <div className="text-xs text-neutral-600 line-clamp-2">{n.body}</div>
            <div className="text-[10px] text-neutral-400 mt-0.5">
              {new Date(n.ts).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
